import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { debounceTime, distinctUntilChanged, skip, tap } from 'rxjs/operators';
import { fromEvent, merge } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { select, Store } from '@ngrx/store';
import { LayoutUtilsService, MessageType, QueryParamsModel } from '../../../../../core/_base/crud';
import {
  Agreement,
  AgreementActionTypes,
  AgreementReminderRequested,
  AgreementsDataSource,
  AgreementsPageRequested,
  RevokeAgreementRequested,
  selectAgreementError,
  selectAgreementLatestSuccessfullAction,
} from '../../../../../core/agreement';
import { AppState } from '../../../../../core/reducers';
import { ConfirmRevokeDialogComponent } from './confirm-revoke-dialog/confirm-revoke-dialog.component';
import { PdfViewDialogComponent } from '../pdf-view/pdf-view';
import { AgreementService } from 'src/app/core/agreement/_services';
import { FormBuilder, FormGroup } from '@angular/forms';
import { NavigationService } from '../../../../../core/services/navigation.service';
import { ActivatedRoute } from '@angular/router';
import * as moment from 'moment';
import { Moment } from 'moment';
import { MatDatepicker } from '@angular/material/datepicker';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import {
  MAT_MOMENT_DATE_ADAPTER_OPTIONS,
  MomentDateAdapter,
} from '@angular/material-moment-adapter';
import { MY_FORMATS } from '../../../apps/lists/patients/patient-edit/patient-edit.component';

@Component({
  selector: 'kt-agreement-list',
  templateUrl: './agreement-list.component.html',
  styleUrls: ['./agreement-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: DateAdapter,
      useClass: MomentDateAdapter,
      deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS],
    },
    {
      provide: MAT_DATE_FORMATS,
      useValue: MY_FORMATS,
    },
  ],
})
export class AgreementListComponent implements OnInit, OnDestroy {
  dataSource: AgreementsDataSource;
  displayedColumns = [
    'id',
    'name',
    'type',
    'status',
    'dateSent',
    'voidDate',
    'voidReason',
    'actions',
  ];
  searchBarColumns = [
    'searchID',
    'searchName',
    'searchType',
    'searchStatus',
    'searchDateSent',
    'searchVoidDate',
    'searchVoidReason',
    'searchActions',
  ];
  showSearchBar = false;
  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;
  @ViewChild('sort', { static: true }) sort: MatSort;
  @ViewChild('searchInput', { static: true }) searchInput: ElementRef;
  searchIDInput = '';
  searchNameInput = '';
  searchTypeInput = '';
  searchStatusSelect = '';
  searchVoidReasonInput = '';
  searchDateSentInput: Moment | any;
  searchVoidDateInput: Moment | any;
  @ViewChild('searchDateSentPicker') searchDateSentPicker: MatDatepicker<Moment>;
  @ViewChild('searchVoidDatePicker') searchVoidDatePicker: MatDatepicker<Moment>;
  private subscriptions: any[] = [];
  searchForm: FormGroup;
  filtersApplied: any = false;
  firstLoadAfterBack: any = false;
  pageSize: any;

  constructor(
    private store: Store<AppState>,
    public dialog: MatDialog,
    public snackBar: MatSnackBar,
    private layoutUtilsService: LayoutUtilsService,
    private translate: TranslateService,
    private agreementService: AgreementService,
    private navigationService: NavigationService,
    private activatedRoute: ActivatedRoute,
    private fb: FormBuilder,
  ) {}

  ngOnInit() {
    // Comment by Nourhan: please don't change the order of this line
    this.filtersApplied = this.navigationService.getFilters();

    const sortSubscription = this.sort.sortChange.subscribe(() => {
      this.paginator.pageIndex = this.paginator?.pageIndex || 0;
      this.sort.active = this.sort.direction ? this.sort.active : 'id';
      this.sort.direction = this.sort.direction || 'desc';
    });
    this.subscriptions.push({ id: 1, sub: sortSubscription });

    const paginatorSubscriptions = merge(this.sort.sortChange, this.paginator.page)
      .pipe(
        tap(() => {
          this.loadAgreementsList();
        }),
      )
      .subscribe();
    this.subscriptions.push({ id: 2, sub: paginatorSubscriptions });

    const searchSubscription = fromEvent(this.searchInput.nativeElement, 'keyup')
      .pipe(
        debounceTime(700),
        distinctUntilChanged(),
        tap(() => {
          this.paginator.pageIndex = 0;
          this.loadAgreementsList();
        }),
      )
      .subscribe();
    this.subscriptions.push({ id: 3, sub: searchSubscription });

    this.dataSource = new AgreementsDataSource(this.store);

    this.searchForm = this.fb.group({
      search: [],
    });

    // Comment by Nourhan: please don't change the order of this scope
    if (this.filtersApplied) {
      this.firstLoadAfterBack = true;
      this.pageSize = this.filtersApplied.pageSize;
    }

    const routeSubscription = this.activatedRoute.queryParams.subscribe(() => {
      this.loadAgreementsList();
    });
    this.subscriptions.push({ id: 5, sub: routeSubscription });
    this.subscriptions.push(
      this.store.pipe(select(selectAgreementError), skip(1)).subscribe((res) => {
        if (res) {
          this.layoutUtilsService.generalDialog(
            '',
            `
            <div style="height: 200px" class="d-flex flex-column flex-center">
              <i style="font-size: 80px" class="flaticon-warning text-danger"></i>
              <p class="font-weight-bold text-center">${
                res.error?.errors[0]?.message || this.translate.instant('GLOBAL.UNSPECIFIED_ERROR')
              }</p>
            </div>
          `,
            '',
            '',
            'Close',
            false,
            true,
            '400px',
          );
        }
      }),
    );
    this.subscriptions.push(
      this.store.pipe(select(selectAgreementLatestSuccessfullAction), skip(1)).subscribe((res) => {
        if (res && res.action_type) {
          let successMsg: string;
          let msgType: MessageType;
          switch (res.action_type) {
            case AgreementActionTypes.AgreementCreated:
              this.reloadAgreementsList();
              successMsg = this.translate.instant('AGREEMENT.EDIT.ADD_MESSAGE');
              msgType = MessageType.Create;
              break;
            case AgreementActionTypes.AgreementUpdated:
              this.reloadAgreementsList();
              successMsg = this.translate.instant('AGREEMENT.EDIT.UPDATE_MESSAGE');
              msgType = MessageType.Update;
              break;
            case AgreementActionTypes.AgreementsDeleted:
              this.reloadAgreementsList();
              successMsg = this.translate.instant('AGREEMENT.DELETE_AGREEMENT_MULTI.MESSAGE');
              msgType = MessageType.Delete;
              break;
            case AgreementActionTypes.AgreementDeleted:
              successMsg = this.translate.instant('AGREEMENT.DELETE_AGREEMENT_SIMPLE.MESSAGE');
              msgType = MessageType.Delete;
              this.reloadAgreementsList();
              break;
            case AgreementActionTypes.AgreementReminded:
              successMsg = this.translate.instant('AGREEMENT.REMIND_AGREEMENT.MESSAGE');
              msgType = MessageType.Update;
              this.reloadAgreementsList();
              break;
            case AgreementActionTypes.AgreementRevoked:
              successMsg = this.translate.instant('AGREEMENT.REVOKE_AGREEMENT.MESSAGE');
              msgType = MessageType.Update;
              this.reloadAgreementsList();
              break;
          }
          if (successMsg) {
            this.layoutUtilsService.showActionNotification(successMsg, msgType, 3000, true, false);
          }
        }
      }),
    );
  }

  ngOnDestroy() {
    this.subscriptions.forEach((el) => {
      if (el.sub) {
        el.sub.unsubscribe();
      }
    });
  }

  loadAgreementsList() {
    const queryParams = new QueryParamsModel(
      this.filterConfiguration(),
      this.sort.direction || 'desc',
      this.sort.active || 'id',
      this.paginator.pageIndex,
      this.paginator.pageSize || this.pageSize || 25,
    );
    // Call request from server
    this.store.dispatch(new AgreementsPageRequested({ page: queryParams }));
  }

  filterConfiguration(reset_applied_flag?: any): any {
    const filter: any = {};
    if (this.filtersApplied && this.firstLoadAfterBack) {
      this.firstLoadAfterBack = false;
      const appliedFilters = this.filtersApplied;
      this.showSearchBar = appliedFilters.showSearchBar;
      if (this.showSearchBar) {
        this.searchIDInput = appliedFilters.id;
        this.searchNameInput = appliedFilters.name;
        this.searchStatusSelect = appliedFilters.status;
        this.searchTypeInput = appliedFilters.type;
        this.searchVoidReasonInput = appliedFilters.reason;
        if (appliedFilters.voidDate) {
          this.searchVoidDateInput = moment(appliedFilters.voidDate);
        } else {
          this.searchVoidDateInput = '';
          this.searchVoidDatePicker?.select(undefined);
        }
        if (appliedFilters.dateSent) {
          this.searchDateSentInput = moment(appliedFilters.dateSent);
        } else {
          this.searchDateSentInput = '';
          this.searchDateSentPicker?.select(undefined);
        }
      } else {
        if (this.searchInput?.nativeElement) {
          this.searchInput.nativeElement.value = appliedFilters.searchTerm || '';
        }
      }
      this.sort.direction = this.filtersApplied?.sortDirection || 'desc';
      this.sort.active = this.filtersApplied?.sortActive || 'name';
      this.paginator.pageIndex = this.filtersApplied?.pageIndex || 0;
      this.paginator.pageSize = this.filtersApplied?.pageSize || 25;
    }
    const dateSentIsMoment = moment.isMoment(this.searchDateSentInput);
    const voidDateIsMoment = moment.isMoment(this.searchVoidDateInput);

    if (reset_applied_flag) this.filtersApplied = false;
    if (this.showSearchBar) {
      filter.id = this.searchIDInput;
      filter.name = this.searchNameInput;
      filter.type = this.searchTypeInput;
      filter.status = this.searchStatusSelect;
      filter.reason = this.searchVoidReasonInput;
      filter.dateSent = this.formatDate(
        dateSentIsMoment ? this.searchDateSentInput.format('MM-DD-YYYY') : '',
      );
      filter.voidDate = this.formatDate(
        voidDateIsMoment ? this.searchVoidDateInput.format('MM-DD-YYYY') : '',
      );
      filter.showSearchBar = true;
    } else {
      filter.searchTerm = this.searchInput.nativeElement.value || '';
      filter.showSearchBar = false;
    }
    filter.sortDirection = this.sort.direction || 'desc';
    filter.sortActive = this.sort.active || 'name';
    filter.pageIndex = this.paginator.pageIndex;
    filter.pageSize = this.paginator.pageSize;
    return filter;
  }

  formatDate(date: string): string {
    if (!date) return null;

    const dateList = date.split('-');

    dateList.unshift(dateList.pop());

    return dateList.join('-');
  }

  reloadAgreementsList(): void {
    this.paginator.pageIndex = 0;

    this.loadAgreementsList();
  }

  getStatusClass(status: string): string {
    if (['sent', 'signed', 'reminded'].includes(status)) {
      return 'warning';
    } else if (status == 'completed') {
      return 'success';
    } else if (status == 'revoked') {
      return 'danger';
    }

    return 'muted';
  }

  getStatusLabel(status: string): string {
    const translateLabel = `AGREEMENT.STATUS.${status.toUpperCase()}`;

    return this.translate.instant(translateLabel);
  }

  revokeAgreementButtonOnClick(agreement: Agreement) {
    const dialogRef = this.dialog.open(ConfirmRevokeDialogComponent, {
      width: '450px',
      disableClose: true,
      closeOnNavigation: true,
    });

    dialogRef.afterClosed().subscribe((res) => {
      if (!res) return;

      this.store.dispatch(new RevokeAgreementRequested({ agreement, reason: res.reason }));
    });
  }

  remindAgreementButtonOnClick(agreement: Agreement) {
    const title: string = this.translate.instant('AGREEMENT.REMIND_AGREEMENT.TITLE');
    const description: string = this.translate.instant('AGREEMENT.REMIND_AGREEMENT.DESCRIPTION');
    const waitDescription: string = this.translate.instant(
      'AGREEMENT.REMIND_AGREEMENT.WAIT_DESCRIPTION',
    );

    const dialogRef = this.layoutUtilsService.confirmAction(title, description, waitDescription);
    dialogRef.afterClosed().subscribe((res) => {
      if (!res) return;

      this.store.dispatch(new AgreementReminderRequested({ agreement }));
    });
  }

  viewAgreementButtonOnClick(agreement: Agreement) {
    this.agreementService.getAgreement(agreement.id).subscribe((res) => {
      const dialogConfig = new MatDialogConfig();

      dialogConfig.closeOnNavigation = true;
      dialogConfig.height = '80vh';
      dialogConfig.maxHeight = '80vh';
      dialogConfig.width = '75vh';
      dialogConfig.maxWidth = '75vh';
      dialogConfig.data = { url: res.url };

      this.dialog.open(PdfViewDialogComponent, dialogConfig);
    });
  }

  getReminderButtonState(agreementObj: Agreement): { disabled: boolean } {
    const agreeInstance = Agreement.fromObject(agreementObj);

    let disabled: boolean;

    if (['COMPLETED', 'REVOKED'].includes(agreeInstance.status.toUpperCase())) {
      disabled = true;
    } else {
      disabled = agreeInstance.isLastReminderWithin24Hours();
    }

    return { disabled };
  }

  getReminderMatTooltip(agreementObj: Agreement) {
    let tooltip = this.translate.instant('AGREEMENT.REMIND_AGREEMENT.TOOLTIP');
    const agreement = Agreement.fromObject(agreementObj);
    let shouldDisplayHoursUntilNextReminder = true;

    if (['COMPLETED', 'REVOKED'].includes(agreement.status.toUpperCase())) {
      // Don't display the hours remaining for the next reminder if the agreement is completed or revoked.
      shouldDisplayHoursUntilNextReminder = false;
    }

    const nextReminder = agreement.getHoursLeftForNextReminder();

    tooltip =
      nextReminder > 0 && shouldDisplayHoursUntilNextReminder
        ? `${tooltip} (${nextReminder}h)`
        : tooltip;

    return tooltip;
  }

  resetSearchBar(event?: Event): void {
    event?.stopPropagation();
    this.searchNameInput = '';
    this.searchIDInput = '';
    this.searchNameInput = '';
    this.searchTypeInput = '';
    this.searchStatusSelect = '';
    this.searchVoidReasonInput = '';
    this.searchDateSentInput = '';
    this.searchVoidDateInput = '';
    this.searchDateSentPicker?.select(undefined);
    this.searchVoidDatePicker?.select(undefined);
  }

  resetSearchInput(event: Event, inputEl: HTMLInputElement, reload = false): void {
    event.stopPropagation();

    inputEl.value = '';

    if (reload) this.reloadAgreementsList();
  }

  toggleSearchBar(event: Event): void {
    event.stopPropagation();

    this.showSearchBar = !this.showSearchBar;

    if (this.showSearchBar) {
      this.searchInput.nativeElement.value = '';
      this.searchInput.nativeElement.disabled = true;
    } else {
      this.resetSearchBar();
      this.searchInput.nativeElement.disabled = false;
      this.searchInput.nativeElement.focus();
    }

    this.reloadAgreementsList();
  }

  handleSearchByField(event: Event): void {
    event.stopPropagation();
    this.reloadAgreementsList();
  }
}
